/**
 * PartnerFilterPanel — E-93 Story 93.1 AC-4.
 *
 * Two-column filter UI for the bulk-email partner picker. The component is
 * dumb: it owns no state beyond what `filters` gives it and emits the full
 * new `EligiblePartnerFilters` shape on every change. Parent owns the
 * debounce + query lifecycle.
 */

import type {
  EligiblePartnerFilters,
  HasActiveRentalFilter,
  HasOutstandingFilter,
  LastPurchaseDaysFilter,
  PartnerSegmentDto,
} from '@/api/bulk-email';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@kgc/ui';
import type { JSX } from 'react';

/** Placeholder value used in Select dropdowns for the "no filter" state.
 *  Radix Select does not allow an empty-string value on SelectItem. */
const ANY_VALUE = '__any__';

export interface PartnerFilterPanelProps {
  filters: EligiblePartnerFilters;
  onFilterChange: (next: EligiblePartnerFilters) => void;
}

const SEGMENTS: PartnerSegmentDto[] = ['VIP', 'ACTIVE', 'AT_RISK', 'INACTIVE'];

export function PartnerFilterPanel({
  filters,
  onFilterChange,
}: PartnerFilterPanelProps): JSX.Element {
  const { t } = useTranslation();
  const selectedSegments = filters.segment ?? [];
  const hasEmailOn = filters.hasEmail !== false;
  const marketingOn = filters.marketingConsent === true;

  function toggleSegment(segment: PartnerSegmentDto): void {
    const next = selectedSegments.includes(segment)
      ? selectedSegments.filter(s => s !== segment)
      : [...selectedSegments, segment];
    onFilterChange({
      ...filters,
      segment: next.length > 0 ? next : undefined,
      cursor: undefined,
    });
  }

  function setRental(value: string): void {
    onFilterChange({
      ...filters,
      hasActiveRental: value === ANY_VALUE ? undefined : (value as HasActiveRentalFilter),
      cursor: undefined,
    });
  }

  function setLastPurchase(value: string): void {
    onFilterChange({
      ...filters,
      lastPurchaseDays: value === ANY_VALUE ? undefined : (value as LastPurchaseDaysFilter),
      cursor: undefined,
    });
  }

  function setOutstanding(value: string): void {
    onFilterChange({
      ...filters,
      hasOutstanding: value === ANY_VALUE ? undefined : (value as HasOutstandingFilter),
      cursor: undefined,
    });
  }

  function setHasEmail(checked: boolean): void {
    // `true` = default (email NOT NULL); `false` = drop the filter entirely
    // to include partners with no email on file. The service reads the
    // explicit `false`; `undefined` keeps the default.
    onFilterChange({
      ...filters,
      hasEmail: checked ? undefined : false,
      cursor: undefined,
    });
  }

  function setMarketingConsent(checked: boolean): void {
    onFilterChange({
      ...filters,
      marketingConsent: checked ? true : undefined,
      cursor: undefined,
    });
  }

  function resetFilters(): void {
    // Preserves `limit` (UX preference) but drops everything else, including
    // any active cursor. The default UX state matches the initial render.
    onFilterChange({ limit: filters.limit });
  }

  return (
    <div
      role="region"
      aria-label={t('email.bulkSend.filters.title')}
      className="space-y-4 rounded-md border p-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t('email.bulkSend.filters.title')}</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={resetFilters}
          aria-label={t('email.bulkSend.filters.resetFilters')}
        >
          {t('email.bulkSend.filters.resetFilters')}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Left column — segment multi-select */}
        <div className="space-y-2">
          <Label>{t('email.bulkSend.filters.segmentLabel')}</Label>
          <div className="space-y-1">
            {SEGMENTS.map(segment => {
              const id = `segment-${segment}`;
              return (
                <label
                  key={segment}
                  htmlFor={id}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <Checkbox
                    id={id}
                    checked={selectedSegments.includes(segment)}
                    onCheckedChange={() => toggleSegment(segment)}
                  />
                  {t(`email.bulkSend.filters.segment.${segmentKey(segment)}`)}
                </label>
              );
            })}
          </div>
        </div>

        {/* Right column — dropdown filters */}
        <div className="space-y-2">
          <div className="space-y-1">
            <Label htmlFor="filter-active-rental">
              {t('email.bulkSend.filters.hasActiveRentalLabel')}
            </Label>
            <Select value={filters.hasActiveRental ?? ANY_VALUE} onValueChange={setRental}>
              <SelectTrigger id="filter-active-rental">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_VALUE}>
                  {t('email.bulkSend.filters.hasActiveRental.any')}
                </SelectItem>
                <SelectItem value="yes">
                  {t('email.bulkSend.filters.hasActiveRental.yes')}
                </SelectItem>
                <SelectItem value="no">{t('email.bulkSend.filters.hasActiveRental.no')}</SelectItem>
                <SelectItem value="expiring_7d">
                  {t('email.bulkSend.filters.hasActiveRental.expiring7d')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="filter-last-purchase">
              {t('email.bulkSend.filters.lastPurchaseDaysLabel')}
            </Label>
            <Select value={filters.lastPurchaseDays ?? ANY_VALUE} onValueChange={setLastPurchase}>
              <SelectTrigger id="filter-last-purchase">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_VALUE}>
                  {t('email.bulkSend.filters.lastPurchase.any')}
                </SelectItem>
                <SelectItem value="30">{t('email.bulkSend.filters.lastPurchase.30')}</SelectItem>
                <SelectItem value="60">{t('email.bulkSend.filters.lastPurchase.60')}</SelectItem>
                <SelectItem value="90_plus">
                  {t('email.bulkSend.filters.lastPurchase.90plus')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="filter-outstanding">
              {t('email.bulkSend.filters.hasOutstandingLabel')}
            </Label>
            <Select value={filters.hasOutstanding ?? ANY_VALUE} onValueChange={setOutstanding}>
              <SelectTrigger id="filter-outstanding">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_VALUE}>
                  {t('email.bulkSend.filters.hasOutstanding.any')}
                </SelectItem>
                <SelectItem value="yes">
                  {t('email.bulkSend.filters.hasOutstanding.yes')}
                </SelectItem>
                <SelectItem value="no">{t('email.bulkSend.filters.hasOutstanding.no')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Bottom row — toggles */}
      <div className="flex flex-wrap items-center gap-6 border-t pt-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch
            checked={hasEmailOn}
            onCheckedChange={setHasEmail}
            aria-label={t('email.bulkSend.filters.hasEmailOnly')}
          />
          {t('email.bulkSend.filters.hasEmailOnly')}
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch
            checked={marketingOn}
            onCheckedChange={setMarketingConsent}
            aria-label={t('email.bulkSend.filters.marketingConsentOnly')}
          />
          {t('email.bulkSend.filters.marketingConsentOnly')}
        </label>
      </div>
    </div>
  );
}

function segmentKey(segment: PartnerSegmentDto): string {
  // The i18n key set uses lowerCamelCase (`vip`, `active`, `atRisk`,
  // `inactive`). Derive mechanically from the enum so adding a future
  // segment requires only one place to update.
  if (segment === 'AT_RISK') return 'atRisk';
  return segment.toLowerCase();
}
